<template>
    <div class="optiongroups" v-if="item.hasOptions">
        <template v-for="optionGroup in optionGroups" :key="optionGroup.optionGroupId">
            <ArticleOptionGroup
                v-if="hasOptions(optionGroup)"
                ref="articleOptionGroupComponents"
                :showOohPrices="props.showOohPrices"
                :optionGroup="optionGroup"
                :mainArticleId="item.articleId"
                :showPlusOnPrices="item.price != 0"
                :showInternalNames="props.showInternalNames"
                :showItemImages="props.showItemImages"
                @changed="onOptionGroupChanged"
            />
        </template>

        <div class="loading" v-if="isLoading">
            <div class="loader"></div>
            <div class="lbl">
                {{ translations.addToCartPopover.loadingOptions }}
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { Popup } from "@/components/dialog/Popup";
    import type { IArticleWithOptions } from "@/data/commands/customer/AddArticleToCartCmd";
    import { Factory } from "@/data/dao/Factory";
    import { CustomerDao } from "@/data/dao/service/CustomerDao";
    import type { ICustomerArticleWithOptions, ICustomerOptionGroup } from "@/data/model/Customer";
    import { translations } from "@/shared/localization/translations";
    import { logError } from "@/shared/logging/errors";
    import { computed, onBeforeMount, ref, watch } from "vue";

    import ArticleOptionGroup from "@/components/order/ArticleOptionGroup.vue";

    const customerDao = Factory.getInstance<CustomerDao>(CustomerDao);

    const props = defineProps<{
        item: ICustomerArticleWithOptions;
        locationId?: string;
        offerId?: string;
        offerUseCaseId: string;
        showInternalNames: boolean;
        showOohPrices: boolean;
        showItemImages: boolean;
        offerOptionGroups?: ICustomerOptionGroup[]; //todo: this becomes required once we remove async options loading
    }>();

    const emit = defineEmits<{
        selectionChanged: [selectedOptions: IArticleWithOptions[]];
    }>();

    // state
    const isLoading = ref(true);
    const optionGroups = ref([] as ICustomerOptionGroup[]);

    // template refs
    const articleOptionGroupComponents = ref<InstanceType<typeof ArticleOptionGroup>[]>([]);

    // computed
    const totalPrice = computed(() => {
        let sum = 0;
        for (const optionGroupComponent of articleOptionGroupComponents.value) {
            sum += optionGroupComponent.totalPrice;
        }
        return sum;
    });
    const isValid = computed(() => {
        for (const optionGroupComponent of articleOptionGroupComponents.value) {
            if (!optionGroupComponent.isValid) {
                return false;
            }
        }
        return true;
    });

    // functions
    async function loadOptions() {
        isLoading.value = false;
        optionGroups.value = [];

        try {
            if (!props.item.hasOptions) return;

            // todo: remove this once we confirm that async loading is not needed
            if (props.offerOptionGroups != null) {
                if (props.item.sortedOptionGroups != null) {
                    optionGroups.value = props.item.sortedOptionGroups.map((x) => {
                        const offerOptionGroup = props.offerOptionGroups!.find((y) => y.optionGroupId === x);
                        if (offerOptionGroup != null) {
                            return offerOptionGroup;
                        }
                        throw Error("Offer option group not found!");
                    });
                }
            } else {
                if (props.offerId == null) {
                    throw Error("Offer id is null!");
                }
                if (props.locationId == null) {
                    throw Error("Location id is null!");
                }

                isLoading.value = true;

                const loadedOptionGroups = await customerDao.getOptionsOfArticle({
                    article: props.item.articleId,
                    offerId: props.offerId,
                    useCaseId: props.offerUseCaseId,
                    locationId: props.locationId,
                });
                optionGroups.value = loadedOptionGroups;
            }
        } catch (ex) {
            logError(ex);
            Popup.show({ message: translations.addToCartPopover.failedLoadingOptions });
        } finally {
            isLoading.value = false;
        }
    }

    function getSelectedArticlesWithOptions(): IArticleWithOptions[] {
        const selectedOptions = [] as IArticleWithOptions[];

        for (const optionGroup of articleOptionGroupComponents.value) {
            const selectedArticlesWithOptions = optionGroup.getSelectedArticlesWithOptions();
            for (const selectedArticle of selectedArticlesWithOptions) {
                selectedOptions.push(selectedArticle);
            }
        }

        return selectedOptions;
    }

    function hasOptions(optionGroup: ICustomerOptionGroup) {
        return optionGroup.options.length > 0;
    }

    function onOptionGroupChanged() {
        const selectedArticlesWithOptions = getSelectedArticlesWithOptions();
        emit("selectionChanged", selectedArticlesWithOptions);
    }

    // watchers
    watch(
        () => props.item,
        async () => {
            await loadOptions();
        }
    );

    // onMounted actions
    onBeforeMount(async () => {
        loadOptions();
    });

    // exposed
    defineExpose({
        totalPrice,
        isValid,
        isLoading,
        getSelectedArticlesWithOptions,
    });
</script>

<style scoped lang="scss">
    .optiongroups {
        padding: 0 0 20px 0;

        > .loading {
            text-align: left;
            width: 100%;
            padding: 20px 0;

            > .loader {
                position: relative;
                display: inline-block;
                vertical-align: top;
                width: 30px;
                height: 30px;
                background-image: url("/assets/img/app/loading_green.svg");
            }

            > .lbl {
                display: inline-block;
                vertical-align: top;
                padding-left: 6px;
                letter-spacing: 0.3px;
                color: #aaaaaa;
                font-size: 14px;
                line-height: 30px;
            }
        }
    }
</style>
